import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '@/features/auth/presentation/contexts/AuthContext';
import { useLevelsStore } from '@/shared/stores/levels.store';
import { useAcademicCyclesStore } from '@/shared/stores/academic-cycles.store';
import { useSubjectsStore } from '@/shared/stores/subjects.store';
import { useCoursesStore } from '@/shared/stores/courses.store';
import { useDashboardStatsStore } from '@/shared/stores/dashboard-stats.store';
import { useNotificationsStore, DashboardNotification } from '@/shared/stores/notifications.store';
import { useWorkspaceSettingsStore } from '@/shared/stores/workspace-settings.store';
import { useOnboardingStore } from '@/shared/stores/onboarding.store';
import { useBrandingStore } from '@/shared/stores/branding.store';
import { useWorkspaceAccessStore } from '@/shared/stores/workspace-access.store';
import { useTerminologyStore } from '@/shared/stores/terminology.store';
import { useFeatureFlagsStore } from '@/shared/stores/feature-flags.store';
import { useRankingStore } from '@/shared/stores/ranking.store';
import { getTTL, isStale } from '@/shared/config/cache.config';
import {
  InitializationState,
  InitializationResult,
  InitializationError,
  InitializationTask,
} from '@/shared/types/initialization.types';

// Use Cases
import { GetAcademicCyclesUseCase } from '@/shared/application/use-cases/GetAcademicCyclesUseCase';
import { GetWorkspaceUseCase } from '@/shared/application/use-cases/GetWorkspaceUseCase';
import { GetLevelsUseCase } from '@/features/academic-structure/application/use-cases/GetLevelsUseCase';
import { GetSubjectsUseCase } from '@/features/academic-structure/application/use-cases/GetSubjectsUseCase';
import { GetCoursesUseCase } from '@/features/dashboard/application/use-cases/GetCoursesUseCase';
import { GetNotificationsUseCase } from '@/features/dashboard/application/use-cases/GetNotificationsUseCase';
import { GetDashboardStatsUseCase } from '@/features/dashboard/application/use-cases/GetDashboardStatsUseCase';
import { GetWorkspaceSettingsUseCase } from '@/features/settings/application/use-cases/GetWorkspaceSettingsUseCase';
import { GetOnboardingProgressUseCase } from '@/features/onboarding/application/use-cases/GetOnboardingProgressUseCase';
import { GetBrandingUseCase } from '@/features/settings/application/use-cases/GetBrandingUseCase';
import { GetWorkspaceFlagKeysUseCase } from '@/features/feature-flags/application/use-cases/GetWorkspaceFlagKeysUseCase';
import { GetStudentRankingUseCase } from '@/features/ranking/application/use-cases/GetStudentRankingUseCase';
import { GetStreakUseCase } from '@/features/ranking/application/use-cases/GetStreakUseCase';
import { GetFlashcardAnalyticsUseCase } from '@/features/flashcards/application/use-cases/GetFlashcardAnalyticsUseCase';

// Repositories
import { SupabaseAcademicCyclesRepository } from '@/shared/infrastructure/repositories/SupabaseAcademicCyclesRepository';
import { SupabaseWorkspaceRepository } from '@/shared/infrastructure/repositories/SupabaseWorkspaceRepository';
import { SupabaseAcademicRepository } from '@/features/academic-structure/infrastructure/repositories/SupabaseAcademicRepository';
import { SupabaseDashboardRepository } from '@/features/dashboard/infrastructure/repositories/SupabaseDashboardRepository';
import { SupabaseWorkspaceSettingsRepository } from '@/features/settings/infrastructure/repositories/SupabaseWorkspaceSettingsRepository';
import { SupabaseOnboardingRepository } from '@/features/onboarding/infrastructure/repositories/SupabaseOnboardingRepository';
import { SupabaseBrandingRepository } from '@/features/settings/infrastructure/repositories/SupabaseBrandingRepository';
import { SupabaseFeatureFlagRepository } from '@/features/feature-flags/infrastructure/repositories/SupabaseFeatureFlagRepository';
import { SupabaseRankingRepository } from '@/features/ranking/infrastructure/repositories/SupabaseRankingRepository';
import { SupabaseFlashcardRepository } from '@/features/flashcards/infrastructure/repositories/SupabaseFlashcardRepository';

/**
 * usePostLoginInit Hook
 *
 * Manages post-login initialization in 3 phases:
 * - Phase 1: Critical data (blocks UI)
 * - Phase 2: Secondary data (background)
 * - Phase 3: Lazy-loaded data (on-demand)
 *
 * Strategy: Stale-While-Revalidate with differentiated TTLs
 */
export function usePostLoginInit() {
  const { user } = useAuth();
  const levelsStore = useLevelsStore();
  const cyclesStore = useAcademicCyclesStore();
  const subjectsStore = useSubjectsStore();
  const coursesStore = useCoursesStore();
  const statsStore = useDashboardStatsStore();
  const notificationsStore = useNotificationsStore();
  const workspaceSettingsStore = useWorkspaceSettingsStore();
  const onboardingStore = useOnboardingStore();
  const brandingStore = useBrandingStore();
  const workspaceAccessStore = useWorkspaceAccessStore();
  const terminologyStore = useTerminologyStore();
  const featureFlagsStore = useFeatureFlagsStore();
  const rankingStore = useRankingStore();

  // Initialize repositories and use cases
  const useCases = useMemo(() => {
    const academicCyclesRepo = new SupabaseAcademicCyclesRepository();
    const workspaceRepo = new SupabaseWorkspaceRepository();
    const workspaceIdNum = user?.workspaceId ? Number(user.workspaceId) : 0;
    const academicRepo = new SupabaseAcademicRepository(workspaceIdNum);
    const dashboardRepo = new SupabaseDashboardRepository(); // Using real Supabase repository
    const workspaceSettingsRepo = new SupabaseWorkspaceSettingsRepository();
    const onboardingRepo = new SupabaseOnboardingRepository();
    const brandingRepo = new SupabaseBrandingRepository();
    const featureFlagRepo = new SupabaseFeatureFlagRepository();
    const rankingRepo = new SupabaseRankingRepository();
    const flashcardRepo = new SupabaseFlashcardRepository();

    return {
      getAcademicCycles: new GetAcademicCyclesUseCase(academicCyclesRepo),
      getWorkspace: new GetWorkspaceUseCase(workspaceRepo),
      getLevels: new GetLevelsUseCase(academicRepo),
      getSubjects: new GetSubjectsUseCase(academicRepo),
      getCourses: new GetCoursesUseCase(dashboardRepo),
      getNotifications: new GetNotificationsUseCase(dashboardRepo),
      getDashboardStats: new GetDashboardStatsUseCase(dashboardRepo),
      getWorkspaceSettings: new GetWorkspaceSettingsUseCase(workspaceSettingsRepo),
      getOnboardingProgress: new GetOnboardingProgressUseCase(onboardingRepo),
      getBranding: new GetBrandingUseCase(brandingRepo),
      getWorkspaceFlagKeys: new GetWorkspaceFlagKeysUseCase(featureFlagRepo),
      getStudentRanking: new GetStudentRankingUseCase(rankingRepo),
      getStreak: new GetStreakUseCase(rankingRepo),
      getFlashcardAnalytics: new GetFlashcardAnalyticsUseCase(flashcardRepo),
    };
   
  }, [user?.workspaceId]);

  const [state, setState] = useState<InitializationState>({
    phase: 'idle',
    isInitializing: false,
    isComplete: false,
    hasError: false,
    error: null,
    progress: 0,
    currentTask: null,
    failedTasks: [],
    completedTasks: [],
  });

  /**
   * Execute a single task with error handling and retry logic
   */
  const executeTask = async (task: InitializationTask): Promise<boolean> => {
    let attempts = 0;
    const maxAttempts = task.maxRetries || 3;

    while (attempts < maxAttempts) {
      try {
        setState((prev) => ({ ...prev, currentTask: task.name }));
        await task.execute();
        setState((prev) => ({
          ...prev,
          completedTasks: [...prev.completedTasks, task.id],
        }));
        return true;
      } catch (error) {
        attempts++;
        if (attempts >= maxAttempts) {
          const initError: InitializationError = {
            phase: state.phase,
            task: task.id,
            message: error instanceof Error ? error.message : 'Unknown error',
            isCritical: task.isCritical,
            canRetry: task.retryable,
            originalError: error instanceof Error ? error : undefined,
          };

          setState((prev) => ({
            ...prev,
            failedTasks: [...prev.failedTasks, task.id],
            hasError: true,
            error: initError,
          }));

          if (task.isCritical) {
            throw initError;
          }
          return false;
        }
        // Wait before retry (exponential backoff)
        await new Promise((resolve) => setTimeout(resolve, 1000 * attempts));
      }
    }
    return false;
  };

  /**
   * Phase 1: Critical Data
   * Must complete before showing app
   * OPTIMIZED: Tasks run in parallel for faster initialization
   */
  const executePhase1 = async (): Promise<void> => {
    setState((prev) => ({ ...prev, phase: 'phase1', progress: 10 }));

    const tasks: InitializationTask[] = [
      {
        id: 'current-cycle',
        name: 'Cargando ciclo académico actual',
        isCritical: true,
        retryable: true,
        execute: async () => {
          const ttl = getTTL('academicCycles');
          if (isStale(cyclesStore.lastFetched, ttl)) {
            try {
              const cycle = await useCases.getAcademicCycles.getCurrent(
                user?.workspaceId ? Number(user.workspaceId) : undefined
              );
              if (cycle) {
                cyclesStore.setAcademicCycles([cycle]);
              }
            } catch (error) {
              console.error('Failed to fetch current cycle:', error);
              throw error;
            }
          }
        },
      },
      {
        id: 'user-workspace',
        name: 'Cargando información del workspace',
        isCritical: false,
        retryable: true,
        execute: async () => {
          if (!user?.workspaceId) {
            console.warn('No workspaceId found for user:', user);
            return;
          }
          try {
            const workspaceIdNumber = Number(user.workspaceId);
            const workspace = await useCases.getWorkspace.execute(workspaceIdNumber);

            // Store workspace in localStorage and update access status
            if (workspace) {
              localStorage.setItem('workspace', JSON.stringify(workspace));
              // Update workspace access status in store
              workspaceAccessStore.setAccessStatus(
                workspace.isAccessBlocked,
                workspace.blockedAt,
                workspace.name
              );
            } else {
              console.warn(`No workspace found for ID ${workspaceIdNumber}. User may need to be assigned to a workspace.`);
            }
          } catch (error) {
            console.error('Failed to fetch workspace:', error);
            // Don't throw - this is non-critical
          }
        },
      },
      {
        id: 'workspace-settings',
        name: 'Cargando configuración del workspace',
        isCritical: false,
        retryable: true,
        execute: async () => {
          if (!user?.workspaceId) {
            return;
          }
          try {
            const workspaceIdNumber = Number(user.workspaceId);
            const settings = await useCases.getWorkspaceSettings.execute(workspaceIdNumber);

            if (settings) {
              workspaceSettingsStore.setWorkspaceId(workspaceIdNumber);
              workspaceSettingsStore.setAllowLessonDownloads(settings.features.allowLessonDownloads);
            }
          } catch (error) {
            console.error('Failed to fetch workspace settings:', error);
            // Don't throw - this is non-critical
          }
        },
      },
      {
        id: 'workspace-branding',
        name: 'Cargando personalización de marca',
        isCritical: false,
        retryable: true,
        execute: async () => {
          if (!user?.workspaceId) {
            return;
          }
          try {
            const workspaceIdNumber = Number(user.workspaceId);
            const branding = await useCases.getBranding.execute(workspaceIdNumber);

            if (branding) {
              brandingStore.setBranding(branding);
            }
          } catch (error) {
            console.error('Failed to fetch workspace branding:', error);
            // Don't throw - this is non-critical, app will use default colors
          }
        },
      },
      {
        id: 'workspace-terminology',
        name: 'Cargando terminología del workspace',
        isCritical: false,
        retryable: true,
        execute: async () => {
          if (!user?.workspaceId) {
            return;
          }
          try {
            const workspaceIdNumber = Number(user.workspaceId);
            await terminologyStore.loadFromDB(workspaceIdNumber);
          } catch (error) {
            console.error('Failed to fetch workspace terminology:', error);
            // Non-critical, app will use default labels
          }
        },
      },
      {
        id: 'workspace-feature-flags',
        name: 'Cargando feature flags del workspace',
        isCritical: false,
        retryable: true,
        execute: async () => {
          if (!user?.workspaceId) {
            return;
          }
          try {
            const workspaceIdNumber = Number(user.workspaceId);
            const keys = await useCases.getWorkspaceFlagKeys.execute(workspaceIdNumber);
            featureFlagsStore.setEnabledFlags(keys);
          } catch (error) {
            console.error('Failed to fetch workspace feature flags:', error);
            // Non-critical, features will be hidden by default
          }
        },
      },
    ];

    // OPTIMIZATION: Run all Phase 1 tasks in parallel
    const taskPromises = tasks.map((task) => executeTask(task));
    await Promise.all(taskPromises);

    setState((prev) => ({
      ...prev,
      progress: 40,
    }));
  };

  /**
   * Phase 2: Secondary Data
   * Loads in background while user sees dashboard
   * OPTIMIZED: All tasks run in parallel for faster loading
   */
  const executePhase2 = async (): Promise<void> => {
    setState((prev) => ({ ...prev, phase: 'phase2', progress: 45 }));

    const tasks: InitializationTask[] = [
      {
        id: 'all-levels',
        name: 'Cargando niveles académicos',
        isCritical: false,
        retryable: true,
        execute: async () => {
          const ttl = getTTL('levels');
          if (isStale(levelsStore.lastFetched, ttl)) {
            try {
              const levels = await useCases.getLevels.execute();
              levelsStore.setLevels(levels);
            } catch (error) {
              console.error('Failed to fetch levels:', error);
            }
          }
        },
      },
      {
        id: 'all-cycles',
        name: 'Cargando todos los ciclos académicos',
        isCritical: false,
        retryable: true,
        execute: async () => {
          const ttl = getTTL('academicCycles');
          if (isStale(cyclesStore.lastFetched, ttl)) {
            try {
              const cycles = await useCases.getAcademicCycles.execute(
                user?.workspaceId ? Number(user.workspaceId) : undefined
              );
              cyclesStore.setAcademicCycles(cycles);
            } catch (error) {
              console.error('Failed to fetch all cycles:', error);
            }
          }
        },
      },
      {
        id: 'all-subjects',
        name: 'Cargando materias',
        isCritical: false,
        retryable: true,
        execute: async () => {
          const ttl = getTTL('subjects');
          if (isStale(subjectsStore.lastFetched, ttl)) {
            try {
              const subjects = await useCases.getSubjects.execute();
              subjectsStore.setSubjects(subjects);
            } catch (error) {
              console.error('Failed to fetch subjects:', error);
            }
          }
        },
      },
      {
        id: 'all-courses',
        name: 'Cargando cursos',
        isCritical: false,
        retryable: true,
        execute: async () => {
          // ALWAYS fetch fresh courses - no TTL check
          try {
            if (user?.role) {
              const courses = await useCases.getCourses.execute();
              coursesStore.setCourses(courses);
            }
          } catch (error) {
            console.error('Failed to fetch courses:', error);
          }
        },
      },
      {
        id: 'dashboard-stats',
        name: 'Cargando estadísticas',
        isCritical: false,
        retryable: true,
        execute: async () => {
          const ttl = getTTL('courses');
          if (isStale(statsStore.lastFetched, ttl)) {
            try {
              if (user?.role) {
                const stats = await useCases.getDashboardStats.execute(user.role);
                statsStore.setStats(stats);
              }
            } catch (error) {
              console.error('Failed to fetch stats:', error);
            }
          }
        },
      },
      {
        id: 'onboarding-check',
        name: 'Verificando onboarding',
        isCritical: false,
        retryable: true,
        execute: async () => {
          if (!user?.id || !user?.role) {
            return;
          }
          try {
            // Load onboarding progress from backend
            const progress = await useCases.getOnboardingProgress.execute(user.id, user.role);

            // Update store with progress
            onboardingStore.loadProgress(user.id, user.role);

            // Auto-start onboarding if status is 'pending' (first time user)
            if (progress.status === 'pending' && progress.shouldShowOnboarding) {
              // Delay to ensure dashboard is fully rendered before starting tour
              // The dashboard needs time to load and render role-specific content
              setTimeout(() => {
                onboardingStore.startOnboarding(user.id, user.role);
              }, 1500);
            }
          } catch (error) {
            console.error('Failed to check onboarding status:', error);
            // Non-critical error, don't throw
          }
        },
      },
      {
        id: 'flashcard-analytics',
        name: 'Cargando analytics de flashcards',
        isCritical: false,
        retryable: true,
        execute: async () => {
          // Only load flashcard analytics for admin
          if (user?.role !== 'admin') {
            return;
          }
          try {
            await useCases.getFlashcardAnalytics.execute();
          } catch (error) {
            console.error('Failed to fetch flashcard analytics:', error);
          }
        },
      },
      {
        id: 'student-ranking',
        name: 'Cargando ranking del estudiante',
        isCritical: false,
        retryable: true,
        execute: async () => {
          // Only load ranking for students
          if (!user?.id || user?.role !== 'student') {
            return;
          }
          try {
            rankingStore.setLoadingRanking(true);
            rankingStore.setLoadingStreak(true);

            // Load ranking and streak in parallel
            const [ranking, streak] = await Promise.all([
              useCases.getStudentRanking.execute(user.id),
              useCases.getStreak.execute(user.id),
            ]);

            rankingStore.setCurrentRanking(ranking);
            rankingStore.setStreak(streak);
          } catch (error) {
            console.error('Failed to fetch student ranking:', error);
            // Non-critical error, don't throw
          } finally {
            rankingStore.setLoadingRanking(false);
            rankingStore.setLoadingStreak(false);
          }
        },
      },
    ];

    // OPTIMIZATION: Run all Phase 2 tasks in parallel
    const taskPromises = tasks.map((task) => executeTask(task));
    await Promise.all(taskPromises);

    setState((prev) => ({
      ...prev,
      progress: 90,
    }));
  };

  /**
   * Phase 3: Lazy-loaded Data
   * Loaded in background after main UI is ready
   * OPTIMIZED: Notifications moved here to not block initial load
   */
  const executePhase3 = async (): Promise<void> => {
    setState((prev) => ({ ...prev, phase: 'phase3', progress: 95 }));

    // Load notifications in background - non-blocking
    // Using requestIdleCallback for lowest priority when available
    const loadNotifications = async () => {
      try {
        if (user?.role) {
          const notificationDTOs = await useCases.getNotifications.execute(user.role);

          const mapScopeType = (scopeType?: string): 'info' | 'warning' | 'success' | 'error' => {
            if (!scopeType) return 'info';
            switch (scopeType.toUpperCase()) {
              case 'GLOBAL': return 'info';
              case 'COURSE': return 'success';
              case 'LEVEL': return 'warning';
              case 'USER': return 'info';
              case 'ADMIN': return 'warning'; // Admin notifications shown as warning (attention needed)
              default: return 'info';
            }
          };

          const mappedNotifications: DashboardNotification[] = notificationDTOs.map((dto: { id: string; message: string; time: string; scopeType?: string; isRead?: boolean }) => ({
            id: dto.id,
            message: dto.message,
            time: dto.time,
            type: mapScopeType(dto.scopeType),
            isRead: dto.isRead ?? false,
            createdAt: new Date(),
          }));

          notificationsStore.setNotifications(mappedNotifications);
        }
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      }
    };

    // Use requestIdleCallback if available, otherwise setTimeout
    if ('requestIdleCallback' in window) {
      (window as unknown as { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(() => loadNotifications());
    } else {
      setTimeout(loadNotifications, 100);
    }

    setState((prev) => ({ ...prev, progress: 100 }));
  };

  /**
   * Main initialization function
   */
  const initialize = useCallback(async (): Promise<InitializationResult> => {
    if (!user) {
      return {
        success: false,
        phase: 'idle',
        completedTasks: [],
        failedTasks: [],
        errors: [
          {
            phase: 'idle',
            task: 'auth-check',
            message: 'No authenticated user',
            isCritical: true,
            canRetry: false,
          },
        ],
        partialData: false,
      };
    }

    setState((prev) => ({
      ...prev,
      isInitializing: true,
      phase: 'phase1',
      progress: 0,
      failedTasks: [],
      completedTasks: [],
      hasError: false,
      error: null,
    }));

    try {
      // Execute phases sequentially
      await executePhase1();
      await executePhase2();
      await executePhase3();

      setState((prev) => ({
        ...prev,
        phase: 'complete',
        isInitializing: false,
        isComplete: true,
        progress: 100,
        currentTask: null,
      }));

      return {
        success: true,
        phase: 'complete',
        completedTasks: state.completedTasks,
        failedTasks: state.failedTasks,
        errors: [],
        partialData: state.failedTasks.length > 0,
      };
    } catch (_error) {
      setState((prev) => ({
        ...prev,
        phase: 'error',
        isInitializing: false,
        hasError: true,
      }));

      return {
        success: false,
        phase: 'error',
        completedTasks: state.completedTasks,
        failedTasks: state.failedTasks,
        errors: state.error ? [state.error] : [],
        partialData: state.completedTasks.length > 0,
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  /**
   * Retry failed tasks
   */
  const retry = useCallback(async (_taskId?: string): Promise<void> => {
    // TODO: Implement retry logic
  }, []);

  /**
   * Reset initialization state
   */
  const reset = useCallback((): void => {
    setState({
      phase: 'idle',
      isInitializing: false,
      isComplete: false,
      hasError: false,
      error: null,
      progress: 0,
      currentTask: null,
      failedTasks: [],
      completedTasks: [],
    });
  }, []);

  /**
   * Track if initialization has been triggered for current user
   */
  const hasInitialized = useRef(false);
  const currentUserId = useRef<string | null>(null);

  /**
   * Auto-initialize on mount if user is authenticated
   */
  useEffect(() => {
    console.log('usePostLoginInit: useEffect triggered', {
      user: !!user,
      userId: user?.id,
      hasInitialized: hasInitialized.current,
      currentUserId: currentUserId.current,
      isInitializing: state.isInitializing,
      isComplete: state.isComplete,
    });

    // Reset flag if user changed
    if (user?.id !== currentUserId.current) {
      console.log('usePostLoginInit: User changed, resetting initialization flag');
      hasInitialized.current = false;
      currentUserId.current = user?.id || null;
    }

    // Only initialize once per user session
    // For non-ADMIN users, also require a valid workspaceId to prevent NaN errors
    const hasValidWorkspace = user?.role === 'admin' || (user?.workspaceId && user.workspaceId !== 'null');

    if (user && hasValidWorkspace && !hasInitialized.current && !state.isInitializing && !state.isComplete) {
      console.log('usePostLoginInit: Starting initialization');
      hasInitialized.current = true;
      initialize();
    } else {
      console.log('usePostLoginInit: Skipping initialization', {
        reason: !user ? 'no user' :
                !hasValidWorkspace ? 'waiting for valid workspaceId' :
                hasInitialized.current ? 'already initialized' :
                state.isInitializing ? 'currently initializing' :
                state.isComplete ? 'already complete' : 'unknown'
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return {
    state,
    initialize,
    retry,
    reset,
  };
}
